import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation.js';
import * as utils from '../common/Utils.js';
import { useConfig } from '../contexts/ConfigContext.js';
import { doc, getDoc, updateDoc, setDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase.js';

import { useDraftData, computeDraftPicksFromDraft } from '../hooks/useDraftData.js';
import DraftBoard from './draft/DraftBoard.js';
import DraftablePlayersList from './draft/DraftablePlayersList.js';
import DraftInitPanel from './draft/DraftInitPanel.js';
import DraftScheduleEditor from './draft/DraftScheduleEditor.js';

import {
  Chart as ChartJS,
  BarController,
  LineController,
  BarElement,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  BarController, LineController, BarElement, LineElement,
  CategoryScale, LinearScale, PointElement, Tooltip, Legend
);

const AdminDraft = () => {
  const navigate = useNavigate();

  const { seasonID } = useParams();
  const config = useConfig();
  const SEASON = seasonID || config.currentSeason;
  const isPastDraft = parseInt(SEASON) < parseInt(config.currentSeason);

  const {
    draft, players, games, allGameStats, teams, seasonTeams,
    signups, draftTimes, computedDraftPicks, dataReady, draftExists, refresh,
  } = useDraftData(SEASON);

  const [scheduleKey, setScheduleKey] = useState(0);
  const [showInitPanel, setShowInitPanel] = useState(false);
  const [showAddPlayers, setShowAddPlayers] = useState(false);
  const [selectedNewPlayers, setSelectedNewPlayers] = useState([]);
  const [addingPlayers, setAddingPlayers] = useState(false);

  const seasonNumbers = Array.from(
    { length: parseInt(config.currentSeason) },
    (_, i) => String(i + 1)
  );

  const currentSeasonGamesPlayed = games.filter(
    g => String(g.season) === String(SEASON) && g.status === 'completed'
  ).length;

  const showPerformanceCharts = isPastDraft || currentSeasonGamesPlayed > 0;

  const draftPicks = computedDraftPicks.filter(p => p.playerID && p.playerID !== '');

  const scrollToCurrentPick = () => {
    const el = document.getElementById('current-pick');
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 250;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const copyCoreEmails = () => {
    const emails = (draft.corePlayers || [])
      .map(id => utils.getPlayerByID(players, id))
      .filter(Boolean)
      .map(p => p.email)
      .join(', ');
    navigator.clipboard.writeText(emails)
      .then(() => alert('Emails copied to clipboard!'))
      .catch(() => alert('Failed to copy emails.'));
  };

  const handleDraftPlayer = async (playerID, pickIndex) => {
    const draftRef = doc(db, 'draft', SEASON);
    const snap = await getDoc(draftRef);
    const current = snap.data().draftedPlayers || [];

    const updated = [...current];
    updated[pickIndex] = playerID;
    await updateDoc(draftRef, { draftedPlayers: updated });

    const pick = computedDraftPicks[pickIndex];
    if (pick && pick.teamID) {
      const stID = utils.seasonTeamID(pick.teamID, SEASON);
      await setDoc(doc(db, 'seasonTeams', stID), { players: arrayUnion(playerID) }, { merge: true });
    }

    refresh();
  };

  const handleScheduleSaved = (newSchedule) => {
    setScheduleKey(k => k + 1);
    refresh();
  };

  const existingDraftableIDs = new Set([
    ...(draft.undraftedPlayers || []).map(p => p.id),
    ...(draft.corePlayers || []),
  ]);

  const eligibleNewPlayers = signups
    .filter(s => s.paid && !existingDraftableIDs.has(s.player_id))
    .map(s => utils.getPlayerByID(players, s.player_id))
    .filter(Boolean)
    .sort((a, b) => {
      const la = (a.lastName || '').toLowerCase();
      const lb = (b.lastName || '').toLowerCase();
      return la < lb ? -1 : la > lb ? 1 : 0;
    });

  const handleAddPlayers = async () => {
    if (selectedNewPlayers.length === 0) return;
    setAddingPlayers(true);
    try {
      const draftRef = doc(db, 'draft', SEASON);
      const snap = await getDoc(draftRef);
      const current = snap.data();
      await updateDoc(draftRef, {
        undraftedPlayers: [...(current.undraftedPlayers || []), ...selectedNewPlayers],
        draftedPlayers: [...(current.draftedPlayers || []), ...selectedNewPlayers.map(() => '')],
      });
      setSelectedNewPlayers([]);
      setShowAddPlayers(false);
      refresh();
    } finally {
      setAddingPlayers(false);
    }
  };

  // ── Performance chart helpers ──────────────────────────────────────────────

  function linearRegression(data) {
    const n = data.length;
    if (n === 0) return { m: 0, b: 0 };
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    data.forEach(({ pick, ppg }) => {
      sumX += pick; sumY += ppg;
      sumXY += pick * ppg; sumX2 += pick * pick;
    });
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    return { m: slope, b: intercept };
  }

  function showDraftPerformanceChart() {
    const PPG_W = 0.65, RPG_W = 0.25, APG_W = 0.10;

    const draftData = draftPicks
      .map(pick => {
        const playerStats = allGameStats.filter(
          s => s.playerID === pick.playerID && s.status === 'played' &&
            utils.getGameForID(games, s.gameID)?.season == SEASON
        );
        if (playerStats.length === 0) return null;
        const gp = playerStats.length;
        const ppg = playerStats.reduce((s, x) => s + x.points, 0) / gp;
        const rpg = playerStats.reduce((s, x) => s + x.rebounds, 0) / gp;
        const apg = playerStats.reduce((s, x) => s + x.assists, 0) / gp;
        const player = utils.getPlayerByID(players, pick.playerID);
        return {
          pick: pick.number,
          name: player ? utils.displayPlayerName(player) : 'Unknown',
          team: pick.team,
          ppg: Number(ppg.toFixed(2)),
          rpg: Number(rpg.toFixed(2)),
          apg: Number(apg.toFixed(2)),
          playerID: pick.playerID,
        };
      })
      .filter(Boolean);

    if (draftData.length === 0) return null;

    const labels = draftData.map(d => d.name);

    const { m: ppg_m, b: ppg_b } = linearRegression(draftData.map(d => ({ pick: d.pick, ppg: d.ppg })));
    const { m: rpg_m, b: rpg_b } = linearRegression(draftData.map(d => ({ pick: d.pick, ppg: d.rpg })));
    const { m: apg_m, b: apg_b } = linearRegression(draftData.map(d => ({ pick: d.pick, ppg: d.apg })));

    const ppgTrend = draftData.map(d => ppg_m * d.pick + ppg_b);
    const rpgTrend = draftData.map(d => rpg_m * d.pick + rpg_b);
    const apgTrend = draftData.map(d => apg_m * d.pick + apg_b);

    const topOutperformers = draftData
      .map(d => {
        const ppgDiff = d.ppg - (ppg_m * d.pick + ppg_b);
        const rpgDiff = d.rpg - (rpg_m * d.pick + rpg_b);
        const apgDiff = d.apg - (apg_m * d.pick + apg_b);
        const combinedDiff = ppgDiff * PPG_W + rpgDiff * RPG_W + apgDiff * APG_W;
        return { ...d, ppgDiff: Number(ppgDiff.toFixed(2)), rpgDiff: Number(rpgDiff.toFixed(2)), apgDiff: Number(apgDiff.toFixed(2)), combinedDiff: Number(combinedDiff.toFixed(2)) };
      })
      .filter(d => d.combinedDiff > 0)
      .sort((a, b) => b.combinedDiff - a.combinedDiff)
      .slice(0, 10);

    const baseOptions = {
      responsive: true,
      plugins: {
        legend: { position: 'top' },
        tooltip: {
          mode: 'index', intersect: false,
          callbacks: { title: items => `Pick #${draftData[items[0].dataIndex].pick} — ${draftData[items[0].dataIndex].name}` },
        },
      },
      scales: {
        x: { ticks: { autoSkip: true, maxRotation: 45, minRotation: 45 }, title: { display: true, text: 'Draft Pick (Player)' } },
        y: { title: { display: true, text: 'Per Game' } },
      },
    };

    const teamGradesMap = {};
    draftPicks.forEach(pick => {
      const d = draftData.find(x => x.pick === pick.number);
      if (!d || !pick.team) return;
      const ppgDiff = d.ppg - (ppg_m * pick.number + ppg_b);
      const rpgDiff = d.rpg - (rpg_m * pick.number + rpg_b);
      const apgDiff = d.apg - (apg_m * pick.number + apg_b);
      const combinedDiff = ppgDiff * PPG_W + rpgDiff * RPG_W + apgDiff * APG_W;
      if (!teamGradesMap[pick.team.id]) {
        teamGradesMap[pick.team.id] = { team: pick.team, totalPPGDiff: 0, totalRPGDiff: 0, totalAPGDiff: 0, totalCombinedDiff: 0, picks: 0 };
      }
      teamGradesMap[pick.team.id].totalPPGDiff += ppgDiff;
      teamGradesMap[pick.team.id].totalRPGDiff += rpgDiff;
      teamGradesMap[pick.team.id].totalAPGDiff += apgDiff;
      teamGradesMap[pick.team.id].totalCombinedDiff += combinedDiff;
      teamGradesMap[pick.team.id].picks += 1;
    });

    function gradeFromDiff(diff) {
      if (diff >= 4) return 'A+';
      if (diff >= 2.5) return 'A';
      if (diff >= 1) return 'A-';
      if (diff >= 0.25) return 'B+';
      if (diff >= -0.25) return 'B';
      if (diff >= -1) return 'B-';
      if (diff >= -2.5) return 'C';
      if (diff >= -4) return 'D';
      return 'F';
    }

    const gradedTeams = Object.values(teamGradesMap)
      .map(e => ({
        team: e.team,
        avgPPGDiff: Number((e.totalPPGDiff / e.picks).toFixed(2)),
        avgRPGDiff: Number((e.totalRPGDiff / e.picks).toFixed(2)),
        avgAPGDiff: Number((e.totalAPGDiff / e.picks).toFixed(2)),
        avgCombinedDiff: Number((e.totalCombinedDiff / e.picks).toFixed(2)),
        grade: gradeFromDiff(e.totalCombinedDiff / e.picks),
      }))
      .sort((a, b) => b.avgCombinedDiff - a.avgCombinedDiff);

    const chart = (title, data, datasets) => (
      <>
        <h2>{title}</h2>
        <Bar data={{ labels, datasets }} options={baseOptions} />
      </>
    );

    return (
      <div style={{ width: '100%', maxWidth: 800 }}>
        {chart('Draft Pick vs Average PPG', draftData, [
          { type: 'line', label: 'Expected PPG (Draft Curve)', data: ppgTrend, borderColor: 'rgba(255,99,132,1)', borderWidth: 2, pointRadius: 0, tension: 0.25, order: 1 },
          { type: 'bar', label: 'Average Points Per Game', data: draftData.map(d => d.ppg), backgroundColor: 'rgba(54,162,235,0.6)', order: 2 },
        ])}
        {chart('Draft Pick vs Average RPG', draftData, [
          { type: 'line', label: 'Expected RPG (Draft Curve)', data: rpgTrend, borderColor: 'rgba(255,159,64,1)', borderWidth: 2, pointRadius: 0, tension: 0.25, order: 1 },
          { type: 'bar', label: 'Average Rebounds Per Game', data: draftData.map(d => d.rpg), backgroundColor: 'rgba(75,192,192,0.6)', order: 2 },
        ])}
        {chart('Draft Pick vs Average APG', draftData, [
          { type: 'line', label: 'Expected APG (Draft Curve)', data: apgTrend, borderColor: 'rgba(153,102,255,1)', borderWidth: 2, pointRadius: 0, tension: 0.25, order: 1 },
          { type: 'bar', label: 'Average Assists Per Game', data: draftData.map(d => d.apg), backgroundColor: 'rgba(153,102,255,0.6)', order: 2 },
        ])}

        <div style={{ marginTop: 32 }}>
          <h3>Draft Performance by Team</h3>
          <table className="scroll-on-overflow" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr><th>Team</th><th>Avg +/- PPG</th><th>Avg +/- RPG</th><th>Avg +/- APG</th><th>Draft Grade</th></tr>
            </thead>
            <tbody>
              {gradedTeams.map(({ team, avgPPGDiff, avgRPGDiff, avgAPGDiff, grade }) => (
                <tr key={team.id}>
                  <td>
                    <a className="unstyled-link" href={isPastDraft ? `/teams/${team.id}/seasons/${SEASON}` : `/teams/${team.id}`}>{team.name}</a>
                    {' '}({utils.getRecordForID(games, team.id, SEASON)})
                  </td>
                  <td>{avgPPGDiff > 0 ? '+' : ''}{avgPPGDiff}</td>
                  <td>{avgRPGDiff > 0 ? '+' : ''}{avgRPGDiff}</td>
                  <td>{avgAPGDiff > 0 ? '+' : ''}{avgAPGDiff}</td>
                  <td><strong>{grade}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 40 }}>
          <h3>Top 10 Draft Picks by Pick #</h3>
          <table className="scroll-on-overflow" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr><th>Pick</th><th>Player</th><th>Team</th><th>PPG (+/-)</th><th>RPG (+/-)</th><th>APG (+/-)</th><th>Draft Value</th></tr>
            </thead>
            <tbody>
              {topOutperformers.map(p => (
                <tr key={p.pick}>
                  <td>#{p.pick}</td>
                  <td><a className="unstyled-link" href={`/players/${p.playerID}`}>{p.name}</a></td>
                  <td><a className="unstyled-link" href={`/teams/${p.team?.id}`}>{p.team?.nameAbbv}</a></td>
                  <td>{p.ppg} <span style={{ color: p.ppgDiff >= 0 ? 'green' : 'red' }}>({p.ppgDiff > 0 ? '+' : ''}{p.ppgDiff})</span></td>
                  <td>{p.rpg} <span style={{ color: p.rpgDiff >= 0 ? 'green' : 'red' }}>({p.rpgDiff > 0 ? '+' : ''}{p.rpgDiff})</span></td>
                  <td>{p.apg} <span style={{ color: p.apgDiff >= 0 ? 'green' : 'red' }}>({p.apgDiff > 0 ? '+' : ''}{p.apgDiff})</span></td>
                  <td><strong>{p.combinedDiff > 0 ? '+' : ''}{p.combinedDiff}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!dataReady) {
    return (
      <div className="Schedule">
        <LoadingAnimation />
      </div>
    );
  }

  const noPicks = !draft.draftedPlayers || draft.draftedPlayers.every(id => !id || id === '');

  return (
    <div className="Schedule">
      <div className="league-content">

        {/* Header + season nav */}
        <div className="league-body">
          <div className="league-table-container schedule">
            <p className='league-admin-back-button'><a className='league-link' href="/admin"><b>← Back to Admin Home</b></a></p>
            <p className="league-title-standard-secondary text-align-center">
              Season {SEASON} Draft
              {isPastDraft && <span style={{ fontSize: 14, color: '#888', marginLeft: 10 }}>(past — view only)</span>}
            </p>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 12 }}>
              {seasonNumbers.map(s => (
                <button
                  key={s}
                  className={`btn btn-sm ${s === SEASON ? 'btn-dark' : 'btn-outline-secondary'}`}
                  onClick={() =>
                    navigate(s === config.currentSeason ? '/admin/draft' : `/admin/draft/seasons/${s}`)
                  }
                >
                  Season {s}
                </button>
              ))}
            </div>

            {/* Admin toolbar — current season only */}
            {!isPastDraft && draftExists && (
              <>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 8 }}>
                  <button className="btn btn-dark btn-sm" onClick={copyCoreEmails}>
                    Copy Core Player Emails
                  </button>
                  {noPicks && (
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setShowInitPanel(v => !v)}
                    >
                      {showInitPanel ? 'Cancel Re-initialize' : 'Re-initialize Draft'}
                    </button>
                  )}
                  {eligibleNewPlayers.length > 0 && (
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => { setShowAddPlayers(v => !v); setSelectedNewPlayers([]); }}
                    >
                      {showAddPlayers ? 'Cancel' : `Add Players (${eligibleNewPlayers.length})`}
                    </button>
                  )}
                  <DraftScheduleEditor
                    key={scheduleKey}
                    season={SEASON}
                    currentSchedule={draft.draftSchedule || null}
                    requiredPicks={(draft.undraftedPlayers || []).length}
                    onSaved={handleScheduleSaved}
                  />
                </div>
                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                  <a
                    className="league-type"
                    style={{ textTransform: 'math-auto' }}
                    onClick={scrollToCurrentPick}
                  >
                    Jump to current pick
                  </a>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Add new signed-up players to draftable list */}
        {!isPastDraft && draftExists && showAddPlayers && (
          <div className="league-body">
            <div className="league-table-container schedule" style={{ maxWidth: 500 }}>
              <p className="league-title-standard-small">Add Players to Draft</p>
              <p style={{ color: '#666', fontSize: 14, marginBottom: 12 }}>
                These players are paid and signed up but not yet in the draftable list.
                Only the draftable players list will be updated — picks, core players, and draft order are unchanged.
              </p>
              <div style={{ marginBottom: 8 }}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  type="button"
                  onClick={() => setSelectedNewPlayers(
                    selectedNewPlayers.length === eligibleNewPlayers.length ? [] : [...eligibleNewPlayers]
                  )}
                >
                  {selectedNewPlayers.length === eligibleNewPlayers.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                {eligibleNewPlayers.map(player => {
                  const checked = selectedNewPlayers.some(p => p.id === player.id);
                  return (
                    <label key={player.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setSelectedNewPlayers(prev =>
                          checked ? prev.filter(p => p.id !== player.id) : [...prev, player]
                        )}
                      />
                      {utils.displayPlayerName(player)}
                    </label>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-dark btn-sm"
                  disabled={selectedNewPlayers.length === 0 || addingPlayers}
                  onClick={handleAddPlayers}
                >
                  {addingPlayers ? 'Adding…' : `Add ${selectedNewPlayers.length > 0 ? selectedNewPlayers.length : ''} Player${selectedNewPlayers.length !== 1 ? 's' : ''}`}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowAddPlayers(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Draft not started / re-initialize */}
        {(!draftExists || showInitPanel) && (
          <div className="league-body">
            {!isPastDraft ? (
              <DraftInitPanel
                season={SEASON}
                signups={signups}
                teams={teams}
                players={players}
                initialCorePlayers={draft.corePlayers || []}
                initialDraftOrder={draft.draftOrder || []}
                onSaved={() => { setShowInitPanel(false); refresh(); }}
              />
            ) : (
              <div className="league-table-container schedule">
                <p className="text-align-center" style={{ color: '#666' }}>
                  {isPastDraft
                    ? `No draft data was collected for Season ${SEASON}.`
                    : `The Season ${SEASON} draft has not started yet.`}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Performance charts */}
        {draftExists && showPerformanceCharts && (
          <div className="league-body" style={{ paddingLeft: 16, paddingRight: 16 }}>
            {showDraftPerformanceChart()}
          </div>
        )}

        {/* Draft board + players list */}
        {draftExists && (
          <div className="league-body">
            <div className="league-table-container schedule">
              <DraftBoard
                computedDraftPicks={computedDraftPicks}
                onPickClick={teamID => navigate(isPastDraft ? `/teams/${teamID}/seasons/${SEASON}` : `/teams/${teamID}`)}
              />
            </div>
            <DraftablePlayersList
              draft={draft}
              allGameStats={allGameStats}
              games={games}
              season={parseInt(SEASON)}
              onDraftPlayer={!isPastDraft ? handleDraftPlayer : undefined}
              computedDraftPicks={computedDraftPicks}
            />
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
};

export default AdminDraft;
